import { NOTE_CATEGORIES_MAX, normalizeCategories } from "@ccnote/shared";
import { useId, useState } from "react";
import { useCategories } from "../queries";

/**
 * A note's categories as #tag chips. Type a name and press Enter, space or comma to add it (new names
 * create the category); pick one from the suggestions; × or Backspace on an empty field removes one.
 */
export function CategoryInput({ value, onChange }: { value: string[]; onChange: (value: string[]) => void }) {
  const listId = useId();
  const categories = useCategories();
  const [text, setText] = useState("");
  const full = value.length >= NOTE_CATEGORIES_MAX;

  const add = (input: string) => {
    setText("");
    const next = normalizeCategories([...value, input]);
    if (next.length !== value.length) onChange(next);
  };
  const remove = (slug: string) => onChange(value.filter((s) => s !== slug));
  const suggestions = categories.data?.filter((c) => !value.includes(c.slug)) ?? [];

  return (
    <span className="category-input">
      {value.map((slug) => (
        <span key={slug} className="tag tag-chip">
          #{slug}
          <button type="button" aria-label={`Remove #${slug}`} onClick={() => remove(slug)}>
            ×
          </button>
        </span>
      ))}
      {!full && (
        <input
          list={listId}
          value={text}
          placeholder={value.length ? "add" : "#category"}
          aria-label="Add a category"
          onChange={(e) => {
            const v = e.target.value;
            // A suggestion picked from the list arrives whole: add it right away.
            const picked = (e.nativeEvent as InputEvent).inputType === "insertReplacementText" || !(e.nativeEvent as InputEvent).inputType;
            if (picked && suggestions.some((c) => c.slug === v)) add(v);
            else if (/[\s,，、]$/.test(v)) add(v);
            else setText(v);
          }}
          onBlur={() => text.trim() && add(text)}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return; // Chinese/Japanese input method still choosing
            if (e.key === "Enter") {
              e.preventDefault();
              if (text.trim()) add(text);
            } else if (e.key === "Backspace" && !text && value.length) {
              e.preventDefault();
              remove(value.at(-1)!);
            }
          }}
        />
      )}
      <datalist id={listId}>
        {suggestions.map((c) => <option key={c.slug} value={c.slug} />)}
      </datalist>
    </span>
  );
}
